import { Box, IconButton } from "@mui/material";
import { GoogleMap, MarkerF, useLoadScript } from "@react-google-maps/api";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { BCPCompress, BCPExpand } from "../icons/bcp/BCPIcons";

const libraries = ["places"];

export default function ListMap(props) {
  const {
    originalList,
    filterFunction,
    sx,
    mapSx,
    name,
    zoom,
    onClick,
    hideExpand,
    defaultCenter = { lat: 41.850033, lng: -87.6500523 }, // Fallback: Central USA
    defaultZoom = 5,
  } = props;

  const filterList = (list) => {
    if (filterFunction) {
      return list.filter(filterFunction);
    } else {
      return list;
    }
  };

  const filteredList = filterList(originalList);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getEnvironmentVariable("GOOGLE_API_KEY"),
    libraries,
  });

  const [expanded, setExpanded] = useState(false);

  const mapRef = useRef();
  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;

      // Set the map bounds based on the filtered list
      try {
        const bounds = new window.google.maps.LatLngBounds();
        let validLocations = [];

        filteredList.forEach((item) => {
          const lat =
            item?.coordinate?.[1] || item?.coordinate_point?.coordinates?.[1];
          const lng =
            item?.coordinate?.[0] || item?.coordinate_point?.coordinates?.[0];

          if (lat && lng) {
            const position = { lat, lng };
            bounds.extend(position);
            validLocations.push(position);
          }
        });

        if (validLocations.length > 1) {
          map.fitBounds(bounds, { padding: 50 });
        } else if (validLocations.length === 1) {
          map.setCenter(validLocations[0]);
          map.setZoom(10);
        } else {
          map.setCenter(defaultCenter);
          map.setZoom(defaultZoom);
        }
      } catch (error) {
        console.error("Error setting map bounds:", error);
      }
    },
    [filteredList, defaultCenter, defaultZoom]
  );

  // Memoize the marker icon and size to avoid unnecessary re-renders
  const markerSize = useMemo(() => {
    if (window?.google?.maps?.Size) {
      new window.google.maps.Size(35, 35);
    }
  }, []);
  const markerIcon = useMemo(() => {
    const iconUrl = getEnvironmentVariable("MAP_ICON_IMAGE");
    const markerObj = {
      scaledSize: markerSize,
    };

    // If an icon URL is provided, set it in the marker object otherwise use default marker from Google Maps
    if (iconUrl) {
      markerObj.url = iconUrl;
    }

    return markerObj;
  }, [markerSize]);

  if (loadError) return "Error loading maps";
  if (!isLoaded || !filteredList) return <></>;

  return (
    <Box
      sx={{
        borderRadius: "4px",
        overflow: expanded ? "visible" : "hidden",
        ...sx,
      }}
    >
      <GoogleMap
        id={name || "google-map"}
        mapContainerStyle={
          expanded
            ? {
                ...mapSx,
                width: "100vw",
                height: `calc(100vh - ${
                  window.innerWidth < 900 ? "52px" : "63px"
                })`,
                position: "fixed",
                top: window.innerWidth < 900 ? "52px" : "63px",
                left: 0,
                zIndex: expanded ? 10 : 0,
              }
            : {
                width: "100%",
                height: "30vh",
                position: "relative",
                ...mapSx,
              }
        }
        zoom={zoom || defaultZoom}
        center={defaultCenter}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          maxZoom: 17,
        }}
        onLoad={onMapLoad}
      >
        {!hideExpand && (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                zIndex: 1,
                backgroundColor: "#fff",
                borderRadius: "2px",
                boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
                mr: 1,
                mt: 1,
                color: "#666666",
                "&:hover": {
                  backgroundColor: "#fff",
                  color: "#000",
                },
              }}
            >
              {expanded ? (
                <BCPCompress
                  style={{
                    color: "inherit",
                  }}
                />
              ) : (
                <BCPExpand
                  style={{
                    color: "inherit",
                  }}
                />
              )}
            </IconButton>
          </Box>
        )}

        {originalList?.length &&
          filteredList.map((item, index) => {
            const lat =
              item?.coordinate?.[1] || item?.coordinate_point?.coordinates?.[1];
            const lng =
              item?.coordinate?.[0] || item?.coordinate_point?.coordinates?.[0];

            if (!lat || !lng) {
              return <></>;
            }

            return (
              <MarkerF
                key={index}
                position={{
                  lat: lat,
                  lng: lng,
                }}
                icon={markerIcon}
                onClick={() => {
                  if (onClick) {
                    onClick(lat, lng);
                  }
                }}
              />
            );
          })}
      </GoogleMap>
    </Box>
  );
}
